/**
 * Allowlist sanitiser for the rich text users write into tickets and comments.
 *
 * Ticket bodies are HTML authored by other people, so they are never trusted: everything outside
 * the allowlist below is dropped, including every event handler, `javascript:` URL and embedded
 * script or style. Only the tags the editor can produce survive.
 */

const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'S',
  'CODE',
  'PRE',
  'BLOCKQUOTE',
  'UL',
  'OL',
  'LI',
  'H1',
  'H2',
  'H3',
  'A',
  'IMG',
  'SPAN',
  'DIV',
]);

/** Attributes kept per tag; everything else (including `on*` handlers and `style`) is removed. */
const ALLOWED_ATTRS: Record<string, string[]> = {
  A: ['href', 'title'],
  IMG: ['src', 'alt', 'title', 'width', 'height'],
};

const SAFE_LINK = /^(https?:|mailto:|tel:)/i;
/** The editor stores pasted pictures as data URLs; only images are allowed through. */
const SAFE_IMAGE = /^(https?:|data:image\/(png|jpe?g|gif|webp|avif);base64,)/i;

function clean(node: Element) {
  for (const child of [...node.children]) {
    if (!ALLOWED_TAGS.has(child.tagName)) {
      // Keep the text of an unknown wrapper, drop the element itself; scripts go entirely.
      if (child.tagName === 'SCRIPT' || child.tagName === 'STYLE') child.remove();
      else child.replaceWith(...child.childNodes);
      continue;
    }

    const allowed = ALLOWED_ATTRS[child.tagName] ?? [];
    for (const attr of [...child.attributes]) {
      if (!allowed.includes(attr.name.toLowerCase())) {
        child.removeAttribute(attr.name);
        continue;
      }
      const value = attr.value.trim();
      if (child.tagName === 'A' && attr.name === 'href' && !SAFE_LINK.test(value)) {
        child.removeAttribute(attr.name);
      }
      if (child.tagName === 'IMG' && attr.name === 'src' && !SAFE_IMAGE.test(value)) {
        child.remove();
      }
    }

    // Links always open in a new tab without handing the opener over.
    if (child.tagName === 'A' && child.hasAttribute('href')) {
      child.setAttribute('target', '_blank');
      child.setAttribute('rel', 'noopener noreferrer');
    }

    if (child.isConnected) clean(child);
  }
}

export function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html ?? '', 'text/html');
  clean(doc.body);
  return doc.body.innerHTML;
}
